import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { SepTableComponent } from '../../../../../shared/components/sep-table/sep-table.component';
import { UserCardComponent } from '../../../../../shared/components/user-card/user-card.component';
import { TranslocoModule } from '@jsverse/transloco';
import { Router, ActivatedRoute } from '@angular/router';
import { UserUIModel } from '../../models/UserUIModel';
import { BadgeComponent } from '../../../../../shared/components/badge/badge.component';
import { LicenseStatus } from '../../models/UserDTO';
import { Column } from '../../../../../shared/components/sep-table/Column';
import { TableTargetedAction } from '../../../../../shared/components/sep-table/Action';
import { UserService } from '../../services/user.service';
import { UserCreationNotificationService } from '../../services/user-creation-notification.service';
import { SnackbarService } from '../../../../../core/services/snackbar.service';
import { catchError, EMPTY, Observable, tap } from 'rxjs';
import { Store } from '@ngxs/store';
import { CurrentUserState } from '../../../../../core/store/current-user/current-user.state';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DialogModel } from '../../../../../shared/components/dialog/dialog';
import { CustomPortalError } from '../../../../../core/errors/models/CustomPortalError';
import { EnrollmentNotificationService } from '../../../../../core/services/enrollment-notification.service';
import { AnalyticsService } from '../../../../../core/services/analytics/analytics.service';
import { EditUserFormComponent } from '../edit-user-form/edit-user-form.component';

import { UserDialogService } from '../../services/user-dialog.service';
import { FeatureFlagsService } from '../../../../../core/services/feature-flag.service';
import { CertificateExportService } from '../../../../academy/certificate/services/certificate-export.service';
import { CertificateLanguage } from '../../../../academy/certificate/models/CertificateLanguage';
import { certificateLanguageSubActions } from '../../../../academy/certificate/certificate-language-sub-actions';
import { certificateBundleFileName } from '../../../../academy/certificate/certificate-file-name';

type UserDisplay = UserUIModel & {
  accountStatusTranslationKey: string;
  roleTranslationKey: string | null;
};

@Component({
  selector: 'app-user-table',
  imports: [
    SepTableComponent,
    UserCardComponent,
    TranslocoModule,
    BadgeComponent,
    MatIconModule,
    MatTooltipModule,
    EditUserFormComponent,
  ],
  templateUrl: './user-table.component.html',
})
export class UserTableComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly userService = inject(UserService);
  private readonly userCreationNotificationService = inject(
    UserCreationNotificationService,
  );
  private readonly enrollmentNotificationService = inject(
    EnrollmentNotificationService,
  );
  private readonly snackbarService = inject(SnackbarService);
  private readonly store = inject(Store);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly userDialogService = inject(UserDialogService);
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly certificateExportService = inject(CertificateExportService);

  readonly isCertificateExportEnabled: boolean =
    this.featureFlagsService.isFeatureEnabled(
      this.featureFlagsService.certificates,
    );

  @ViewChild('userCard', { static: true }) userCard!: TemplateRef<{
    $implicit: string;
  }>;
  @ViewChild('licenseBadge', { static: true }) licenseBadge!: TemplateRef<{
    $implicit: string;
  }>;
  @ViewChild('editUserFormTemplate')
  editUserFormTemplate!: TemplateRef<Element>;
  @ViewChild(EditUserFormComponent)
  editUserFormComponent!: EditUserFormComponent;

  @Input({ required: true }) users: UserUIModel[];

  licenseStatus = LicenseStatus;
  targetedUser: UserDisplay | null;
  editUserDialog: DialogModel | null = null;
  licenseUnassignmentConfirmationDialog: DialogModel | null = null;
  deleteUserDialog: DialogModel | null = null;
  upgradeUserDialog: DialogModel | null = null;
  usersToDisplay: UserDisplay[] = [];

  columns: Array<Column<UserDisplay, string | { $implicit: string }>> = [];

  rowActions: Array<TableTargetedAction<UserDisplay>> = [
    {
      label: 'portal.users.table.actions.disable',
      icon: 'manage_accounts',
      translate: true,
      dynamicLabel: (user: UserDisplay) => this.getDynamicLabel(user),
      executeAction: (user: UserDisplay) => {
        if (user.accountEnabled) {
          this.disableUser(user);
        } else {
          this.enableUser(user);
        }
      },
      checkDisabled: (user: UserDisplay) => this.isCurrentUser(user),
    },
    {
      label: 'portal.users.table.actions.edit',
      translate: true,
      icon: 'edit',
      executeAction: (user: UserDisplay) => {
        this.targetedUser = user;
        this.showEditUserDialog();
      },
      checkHide: (user: UserDisplay) => this.hidePromotionAndEditActions(user),
      checkDisabled: (user: UserDisplay) => this.isSyncedUser(user),
    },
    {
      label: 'portal.users.table.actions.exportCertificates',
      translate: true,
      icon: 'workspace_premium',
      checkHide: () => !this.isCertificateExportEnabled,
      subActions: certificateLanguageSubActions(
        (user: UserDisplay, language) => {
          this.exportCertificates(user, language);
        },
      ),
    },
    {
      label: 'portal.users.table.actions.promoteToAdmin',
      translate: true,
      icon: 'upgrade',
      executeAction: (user: UserDisplay) => {
        this.promoteUser(user);
      },
      checkHide: (user: UserDisplay) => this.hidePromotionAndEditActions(user),
    },
    {
      label: 'portal.users.table.actions.delete',
      translate: true,
      icon: 'delete',
      separatorAbove: true,
      tooltipLabel: (user: UserDisplay) => this.getDeleteDisabledTooltip(user),
      executeAction: (user: UserUIModel) => {
        this.showDeleteUserDialog(user);
      },
      checkDisabled: (user: UserDisplay) =>
        this.isCurrentUser(user) ||
        this.isSyncedUser(user) ||
        this.isLicenseUsed(user),
    },
  ];

  ngOnInit(): void {
    this.usersToDisplay = this.users.map((user) => ({
      ...user,
      accountStatusTranslationKey: user.accountEnabled
        ? 'portal.users.table.data.active'
        : 'portal.users.table.data.inactive',
      roleTranslationKey:
        user.role && user.role !== 'unknown'
          ? `portal.users.table.data.${user.role}`
          : 'portal.users.table.data.roleSyncing',
    }));
    this.buildColumns();
    this.cdr.detectChanges();
  }

  showUserCreationDialog() {
    void this.router.navigate(['dialog'], {
      relativeTo: this.activatedRoute,
      queryParams: { step: 'create' },
    });
  }

  showLicenseUnassignmentConfirmationDialog(user: UserDisplay) {
    const unassignLicense = () => {
      this.unassignLicense(user);
      this.licenseUnassignmentConfirmationDialog?.closeDialog();
    };
    this.licenseUnassignmentConfirmationDialog =
      this.userDialogService.showLicenseUnassignmentConfirmationDialog(
        user,
        unassignLicense,
      );
  }

  showEditUserDialog() {
    const confirmEdit = () => {
      if (this.editUserDialog?.buttonRowData) {
        this.editUserDialog.buttonRowData.confirmButtonLoading = true;
      }
      this.updateUser(this.editUserFormComponent.getFormValues());
    };
    this.editUserDialog = this.userDialogService.showEditUserDialog(
      this.editUserFormTemplate,
      confirmEdit,
    );
  }

  unassignLicense(user: UserDisplay) {
    const licenseId = user.license?.id;
    if (!licenseId) {
      console.error('No license assigned to selected user');
      return;
    }
    this.userService.unassignLicense(licenseId).subscribe({
      next: () => {
        this.userCreationNotificationService.setUserChangedNotification();
        this.enrollmentNotificationService.setEnrollmentChanged();
        this.analyticsService.track('license_unassigned');
      },
      error: (error: CustomPortalError) => {
        this.snackbarService.error(error.errorType);
      },
    });
  }

  private handleUserAction(action: () => Observable<void>) {
    action().subscribe({
      next: () => {
        this.userCreationNotificationService.setUserChangedNotification();
      },
    });
  }

  onEditUserFormStatusChanged(isValid: boolean) {
    if (this.editUserDialog?.buttonRowData) {
      this.editUserDialog.buttonRowData.confirmButtonDisabled = !isValid;
    }
  }

  updateUser(updatedUserData: Partial<UserDisplay>) {
    const userId = this.targetedUser?.id;
    if (!userId) {
      console.error('No id for targeted user');
      return;
    }
    this.userService.updateUser(userId, updatedUserData).subscribe({
      next: () => {
        this.userCreationNotificationService.setUserChangedNotification();
        this.editUserDialog?.closeDialog();
      },
      error: (error: CustomPortalError) => {
        this.snackbarService.error(error.errorType);
        this.editUserDialog?.closeDialog();
      },
    });
  }

  private showDeleteUserDialog(user: UserUIModel) {
    this.deleteUserDialog = this.userDialogService.showDeleteConfirmationDialog(
      user,
      () => {
        if (this.deleteUserDialog?.buttonRowData) {
          this.deleteUserDialog.buttonRowData.confirmButtonLoading = true;
        }
        this.userService.deleteUser(user.id).subscribe({
          next: () => {
            this.userCreationNotificationService.setUserChangedNotification();
            this.snackbarService.success(
              'portal.users.deletion.successfulDeletionSnackbar',
              { username: user.username },
            );
            this.analyticsService.track('user_deleted');
            this.deleteUserDialog?.closeDialog();
          },
          error: (error: CustomPortalError) => {
            this.snackbarService.error(error.errorType);
            if (this.deleteUserDialog?.buttonRowData) {
              this.deleteUserDialog.buttonRowData.confirmButtonLoading = false;
            }
          },
        });
      },
    );
  }

  private enableUser(user: UserDisplay) {
    this.handleUserAction(() =>
      this.userService.enable([user]).pipe(
        tap(() => {
          this.analyticsService.track('user_enabled');
        }),
        catchError((error: CustomPortalError) => {
          this.snackbarService.error(error.errorType);
          return EMPTY;
        }),
      ),
    );
  }

  private disableUser(user: UserDisplay) {
    this.handleUserAction(() =>
      this.userService.disable([user]).pipe(
        tap(() => {
          this.analyticsService.track('user_disabled');
        }),
        catchError((error: CustomPortalError) => {
          this.snackbarService.error(error.errorType);
          return EMPTY;
        }),
      ),
    );
  }

  private promoteUser(user: UserDisplay) {
    this.upgradeUserDialog =
      this.userDialogService.showPromoteToAdminConfirmationDialog(user, () => {
        if (this.upgradeUserDialog?.buttonRowData) {
          this.upgradeUserDialog.buttonRowData.confirmButtonLoading = true;
        }

        this.userService.promoteUser(user.id).subscribe({
          next: () => {
            this.userCreationNotificationService.setUserChangedNotification();
            this.upgradeUserDialog?.closeDialog();
            this.snackbarService.success(
              'portal.users.creation.successfulPromoteToAdminSnackBar',
              {
                name: `${user.firstName} ${user.lastName}`,
              },
            );
            this.analyticsService.track('user_promoted');
          },
          error: (error: CustomPortalError) => {
            this.snackbarService.error(error.errorType);
          },
        });
      });
  }

  private exportCertificates(
    user: UserDisplay,
    language: CertificateLanguage,
  ): void {
    this.certificateExportService.exportUserCertificateBundle(
      { userId: user.id, language },
      certificateBundleFileName(language, [user.username]),
    );
  }

  private getDynamicLabel(user: UserDisplay): string {
    if (this.isCurrentUser(user)) {
      return 'portal.users.table.actions.cannotChangeStatusForCurrentUser';
    } else {
      return user.accountEnabled
        ? 'portal.users.table.actions.disable'
        : 'portal.users.table.actions.enable';
    }
  }

  private isCurrentUser(user: UserDisplay) {
    return (
      this.store.selectSnapshot(CurrentUserState.userDetails).email ===
      user.email
    );
  }

  private isSyncedUser(user: UserDisplay): boolean {
    return user.syncStatus === 'Synced';
  }

  private isLicenseUsed(user: UserDisplay): boolean {
    return user.license?.status === LicenseStatus.CONSUMED;
  }

  private getDeleteDisabledTooltip(user: UserDisplay): string {
    return this.isLicenseUsed(user)
      ? 'portal.users.table.actions.deleteDisabledTooltip'
      : '';
  }

  private hidePromotionAndEditActions(user: UserDisplay) {
    return !user.role || user.role === 'admin';
  }

  buildColumns() {
    this.columns = [
      {
        headerName: 'portal.users.table.header.nameAndEmail',
        renderComponent: this.userCard,
        key: 'username',
      },
      {
        headerName: 'portal.users.table.header.role',
        field: 'roleTranslationKey',
        key: 'roleTranslationKey',
        translate: true,
      },
      {
        headerName: 'portal.users.table.header.license',
        renderComponent: this.licenseBadge,
        key: 'license',
      },
      {
        headerName: 'portal.users.table.header.status',
        key: 'accountStatusTranslationKey',
        field: 'accountStatusTranslationKey',
        translate: true,
      },
      {
        headerName: 'portal.users.table.header.added',
        field: 'createdAt',
        key: 'createdAt',
      },
    ];
  }
}
